import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PaperPlaneTilt, WarningOctagon } from "@phosphor-icons/react";

export default function ActivityFlowActions({ projectId, projectCode, fromDept, onDone, initialDocs = [], forwardOnly = false, label }) {
  const [mode, setMode] = useState(null); // 'fwd' | 'esc' | null
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [selDocs, setSelDocs] = useState([]);
  const [groups, setGroups] = useState([]);

  const close = () => { setMode(null); setNote(""); setSel([]); setQ(""); setSelDocs([]); };

  const loadGroups = () => api.get("/recipient-groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));

  useEffect(() => {
    if (mode !== "fwd") return;
    setLoading(true); setSel([]); setQ(""); setSelDocs(initialDocs || []);
    api.get(`/projects/${projectId}/wf-next`)
      .then((r) => setUsers(r.data?.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
    api.get(`/projects/${projectId}/folder`)
      .then((r) => setDocs((r.data || []).filter((f) => f.category !== "email")))
      .catch(() => setDocs([]));
    loadGroups();
  }, [mode, projectId]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleDoc = (id) => setSelDocs((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const applyGroup = (g) => {
    const ids = (g.member_ids || []).filter((id) => users.some((u) => u.id === id));
    setSel(ids);
    if (ids.length) toast.success(`Grup „${g.name}" aplicat (${ids.length} destinatari)`);
    else toast.info(`Niciun membru al grupului „${g.name}" nu e disponibil în listă`);
  };
  const saveGroup = async () => {
    if (sel.length === 0) { toast.error("Bifează întâi destinatarii"); return; }
    const name = (window.prompt("Denumește grupul de destinatari (ex: Echipă producție):") || "").trim();
    if (!name) return;
    try {
      await api.post("/recipient-groups", { name, member_ids: sel });
      toast.success(`Grup salvat: ${name}`);
      loadGroups();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Ștergi grupul „${g.name}"?`)) return;
    try { await api.delete(`/recipient-groups/${g.id}`); loadGroups(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const filtered = users.filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.title || "").toLowerCase().includes(q.toLowerCase()));

  const submit = async () => {
    if (mode === "esc" && !note.trim()) { toast.error("Descrie problema pentru conducere"); return; }
    if (mode === "fwd" && sel.length === 0) { toast.error("Alege cel puțin un destinatar"); return; }
    setBusy(true);
    try {
      if (mode === "fwd") {
        const { data } = await api.post(`/projects/${projectId}/wf-handoff`, { note, from_dept: fromDept, recipient_ids: sel, file_ids: selDocs, send_email: true });
        const emailed = (data.emailed || []).length;
        const failed = (data.recipients || []).filter((r) => r.email_status === "failed").length;
        toast.success(`Transmis către: ${(data.notified || []).join(", ") || "—"}${emailed ? ` · email livrat la ${emailed}` : ""}`);
        if (failed) toast.error(`${failed} email(uri) nu au putut fi livrate`);
        onDone && onDone();
      } else {
        const { data } = await api.post(`/projects/${projectId}/wf-escalate`, { note, from_dept: fromDept });
        toast.success(`Escaladat către conducere: ${(data.notified || []).join(", ") || "—"}`);
      }
      close();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setMode("fwd")} data-testid={`dept-forward-${projectId}`}><PaperPlaneTilt size={13} className="mr-1" /> {label || "Trimite mai departe"}</Button>
      {!forwardOnly && (
      <Button size="sm" variant="outline" className="h-7 border-red-500/40 px-2 text-xs text-red-300 hover:bg-red-500/10" onClick={() => setMode("esc")} data-testid={`dept-escalate-${projectId}`}><WarningOctagon size={13} className="mr-1" /> Escaladează</Button>
      )}
      <Dialog open={!!mode} onOpenChange={(o) => !o && close()}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>{mode === "esc" ? "Escaladează la conducere" : "Trimite mai departe"} — {projectCode}</DialogTitle></DialogHeader>

          {mode === "esc" ? (
            <p className="text-sm text-muted-foreground">Descrie problema pentru conducere (obligatoriu). Directorii (Amin, Alex Militaru) vor fi notificați.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Bifează destinatarii din listă. Toți primesc notificare în aplicație.</p>
              {groups.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="fwd-groups">
                  <span className="text-[11px] text-muted-foreground">Grupuri salvate:</span>
                  {groups.map((g) => (
                    <span key={g.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-white/5 py-0.5 pl-2 pr-1 text-xs">
                      <button type="button" onClick={() => applyGroup(g)} className="hover:text-primary" data-testid={`fwd-group-apply-${g.id}`}>{g.name} ({(g.member_ids || []).length})</button>
                      <button type="button" onClick={() => deleteGroup(g)} className="text-muted-foreground/60 hover:text-red-400" data-testid={`fwd-group-del-${g.id}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută utilizator…" className="h-8" data-testid="fwd-search" />
              {loading && <p className="text-sm text-muted-foreground">Se încarcă utilizatorii…</p>}
              <div className="max-h-56 space-y-0.5 overflow-y-auto rounded-lg border border-border p-2" data-testid="fwd-user-list">
                {filtered.map((u) => (
                  <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-white/5">
                    <Checkbox checked={sel.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`fwd-user-${u.id}`} />
                    <span>{u.name}</span>{u.title && <span className="text-[11px] text-muted-foreground">· {u.title}</span>}
                  </label>
                ))}
                {!loading && filtered.length === 0 && <span className="text-xs text-muted-foreground/70">Niciun utilizator.</span>}
              </div>
              {sel.length > 0 && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-primary" data-testid="fwd-sel-count">{sel.length} selectați</p>
                  <button type="button" onClick={saveGroup} className="text-xs text-muted-foreground underline hover:text-primary" data-testid="fwd-save-group">Salvează ca grup</button>
                </div>
              )}
              {docs.length > 0 && (
                <div className="space-y-1 pt-1">
                  <p className="text-xs text-muted-foreground">Atașează documente (opțional) — vor fi trimise pe email:</p>
                  <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-lg border border-border p-2" data-testid="fwd-doc-list">
                    {docs.map((d) => (
                      <label key={d.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-white/5">
                        <Checkbox checked={selDocs.includes(d.id)} onCheckedChange={() => toggleDoc(d.id)} data-testid={`fwd-doc-${d.id}`} />
                        <span className="truncate">{d.name}</span>
                      </label>
                    ))}
                  </div>
                  {selDocs.length > 0 && <p className="text-xs text-primary">{selDocs.length} documente atașate</p>}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground/80">Destinatarii primesc notificare în aplicație și un email (cu documentele atașate, dacă sunt bifate).</p>
            </div>
          )}

          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={mode === "esc" ? "Descrie problema…" : "Mențiuni pentru destinatari (opțional)…"} data-testid="dept-flow-note" />
          <DialogFooter>
            <Button variant="outline" onClick={close}>Anulează</Button>
            <Button onClick={submit} disabled={busy} className={mode === "esc" ? "bg-red-600 hover:bg-red-700" : ""} data-testid="dept-flow-send">{busy ? "Se trimite…" : (mode === "esc" ? "Trimite la conducere" : "Trimite mai departe")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
